/**
 * LeadManagerHome — Today's pulse for the Lead Manager.
 *
 * Apr 28, 2026 (Squad-D). Screen 01 of the 12-screen Lead Manager flow.
 *
 * Layout:
 *  - 5-KPI strip (inbound, awaiting, ready, outbox, avg-time)
 *  - Big saffron "Verify next" CTA + 30d accuracy donut
 *  - Inbound feed (live · last 10) + Top teams (by today's assignments)
 *
 * Data:
 *  - GET /analytics/lead-manager/today → KPI counts
 *  - GET /analytics/lead-manager/funnel?period=30d → accuracy donut
 *  - GET /leads?verificationStatus=pending_verification&limit=10 → inbound feed
 *  - GET /analytics/lead-manager/team-load → top teams
 *
 * Every fetch goes through safeGet — if Squad-A hasn't shipped the endpoint
 * yet, the section degrades to "No data yet" empty state.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { safeGet } from '@/pages/dashboard/roles/_dashboardShared';
import {
  KpiCard,
  Panel,
  PageShell,
  KbdHint,
  PriorityDot,
  SourceIcon,
  StatusChip,
  Avatar,
  EmptyState,
  type Lead,
  type Team,
  unwrapList,
  leadName,
  leadPhone,
  leadProduct,
  priorityFrom,
  relativeTime,
} from './_lmShared';
import { Inbox, Phone, Check, X, Calendar, Zap } from 'lucide-react';

interface LMSummary {
  inboundToday?: number;
  awaitingVerification?: number;
  awaitingChatkartCount?: number;
  awaitingManualCount?: number;
  oldestPendingMinutes?: number;
  readyToAssign?: number;
  hotCount?: number;
  warmCount?: number;
  reassignmentOutbox?: number;
  avgTimeToAssignMinutes?: number;
  avgDeltaMinutes?: number;
  goalTimeToAssignMinutes?: number;
}

interface Funnel30d {
  conversionRate?: number;
  becameDealsCount?: number;
  didNotConvertCount?: number;
  tenantAvgConversionRate?: number;
}

interface TeamLoad {
  teamId: string;
  teamName: string;
  headName?: string | null;
  assignmentsToday?: number;
  weeklyConversion?: number;
}

export function LeadManagerHome() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const firstName = user?.firstName ?? 'there';

  const [summary, setSummary] = useState<LMSummary>({});
  const [funnel, setFunnel] = useState<Funnel30d>({});
  const [feed, setFeed] = useState<Lead[]>([]);
  const [teams, setTeams] = useState<TeamLoad[]>([]);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingFeed, setLoadingFeed] = useState(true);

  useEffect(() => {
    safeGet<LMSummary>('/analytics/lead-manager/today').then((r) => {
      setSummary(r.data ?? {});
      setLoadingSummary(false);
    });
    safeGet<Funnel30d>('/analytics/lead-manager/funnel?period=30d').then((r) => {
      setFunnel(r.data ?? {});
    });
    // Apr 28 evening — wired to real lead-imports endpoint.
    safeGet<unknown>('/lead-integrations/lead-imports?scope=unverified&limit=10').then((r) => {
      const raw = (r.data as any)?.imports ?? r.data;
      setFeed(unwrapList<Lead>(raw));
      setLoadingFeed(false);
    });
    safeGet<unknown>('/analytics/lead-manager/team-load').then((r) => {
      setTeams(unwrapList<TeamLoad>(r.data).slice(0, 5));
    });
  }, []);

  /* Keyboard shortcut V → /lead-manager/unverified */
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.target as HTMLElement)?.tagName === 'INPUT' || (e.target as HTMLElement)?.tagName === 'TEXTAREA') return;
      if (e.key === 'v' || e.key === 'V') {
        e.preventDefault();
        navigate('/lead-manager/unverified');
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  /* Pick the next lead — first item in the feed. */
  const nextLead = feed[0];

  const today = new Date().toLocaleDateString('en-IN', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const time = new Date().toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  return (
    <PageShell
      title={`Today · ${today}`}
      sub={`Welcome back, ${firstName} · ${time} IST`}
      right={
        <>
          <button className="btn btn-outline">
            <Calendar size={13} /> Last 7 days
          </button>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/lead-manager/unverified')}
          >
            <Zap size={13} /> Verify next <KbdHint k="V" />
          </button>
        </>
      }
    >
      {/* KPI strip */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
          gap: 14,
          marginBottom: 20,
        }}
      >
        <KpiCard
          label="Inbound today"
          value={loadingSummary ? '…' : summary.inboundToday ?? 0}
          delta={
            summary.inboundToday != null
              ? `${summary.inboundToday} this period`
              : undefined
          }
          tone="up"
          sub={loadingFeed ? undefined : feed.length ? `last @ ${relativeTime(feed[0]?.createdAt)} ago` : 'no inbound yet'}
        />
        <KpiCard
          label="Awaiting verification"
          value={loadingSummary ? '…' : summary.awaitingVerification ?? 0}
          delta={
            summary.awaitingChatkartCount != null && summary.awaitingManualCount != null
              ? `${summary.awaitingChatkartCount} ChatKart · ${summary.awaitingManualCount} manual`
              : undefined
          }
          sub={
            summary.oldestPendingMinutes != null
              ? `oldest ${summary.oldestPendingMinutes} min`
              : undefined
          }
        />
        <KpiCard
          label="Ready to assign"
          value={loadingSummary ? '…' : summary.readyToAssign ?? 0}
          delta={
            summary.hotCount != null && summary.warmCount != null
              ? `${summary.hotCount} hot · ${summary.warmCount} warm`
              : undefined
          }
          tone="warn"
        />
        <KpiCard
          label="Reassignment outbox"
          value={loadingSummary ? '…' : summary.reassignmentOutbox ?? 0}
          delta="awaiting admin"
        />
        <KpiCard
          label="Avg time-to-assign"
          value={
            loadingSummary
              ? '…'
              : summary.avgTimeToAssignMinutes != null
              ? `${summary.avgTimeToAssignMinutes} min`
              : '—'
          }
          delta={
            summary.avgDeltaMinutes != null
              ? `${summary.avgDeltaMinutes < 0 ? '↓' : '↑'} ${Math.abs(summary.avgDeltaMinutes)} min vs avg`
              : undefined
          }
          tone={summary.avgDeltaMinutes != null && summary.avgDeltaMinutes < 0 ? 'up' : undefined}
          sub={summary.goalTimeToAssignMinutes != null ? `goal · ${summary.goalTimeToAssignMinutes} min` : undefined}
        />
      </div>

      {/* Big Verify-Next CTA + Accuracy donut */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.4fr 1fr',
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div className="card" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div
            style={{
              padding: '14px 16px',
              borderBottom: '1px solid var(--line)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <div>
              <div style={{ fontWeight: 500, fontSize: 14 }}>The verify loop</div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                Your queue runs FIFO. One-tap to advance.
              </div>
            </div>
            <div style={{ flex: 1 }} />
            <span className="chip">{summary.awaitingVerification ?? 0} in queue</span>
          </div>
          <div style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 24 }}>
            <div
              style={{
                width: 96,
                height: 96,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #C98A3B, #B87A2E)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 28px rgba(184,122,46,.4)',
                flexShrink: 0,
              }}
            >
              <Phone size={36} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--ink-3)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.1,
                  fontWeight: 500,
                }}
              >
                Next up
              </div>
              <div
                style={{
                  fontFamily: 'var(--ff-serif)',
                  fontWeight: 500,
                  fontSize: 26,
                  letterSpacing: -0.3,
                  marginTop: 2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {nextLead ? leadName(nextLead) : 'Queue is clear'}
              </div>
              {nextLead ? (
                <>
                  <div
                    style={{
                      display: 'flex',
                      gap: 14,
                      marginTop: 6,
                      fontSize: 12,
                      color: 'var(--ink-2)',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <SourceIcon source={nextLead.source} size={14} /> {nextLead.source ?? 'Unknown'}
                    </span>
                    <span>·</span>
                    <span>{leadProduct(nextLead)}</span>
                    <span>·</span>
                    <span style={{ color: 'var(--ink-3)' }}>{leadPhone(nextLead)}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate(`/lead-manager/verify/${nextLead.id}`)}
                    >
                      <Check size={13} /> Verify <KbdHint k="V" />
                    </button>
                    <button className="btn btn-outline">
                      <X size={13} /> Reject <KbdHint k="R" />
                    </button>
                    <button className="btn btn-outline">Skip <KbdHint k="N" /></button>
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8 }}>
                  All inbound leads have been verified. Take a breath.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Accuracy donut */}
        <div className="card" style={{ padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div>
              <div style={{ fontWeight: 500, fontSize: 14 }}>Verification accuracy</div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                Last 30 days · % verified that became deals
              </div>
            </div>
            {funnel.conversionRate != null && funnel.tenantAvgConversionRate != null && (
              <span className="chip chip-green">
                {funnel.conversionRate >= funnel.tenantAvgConversionRate ? '↑' : '↓'}{' '}
                {Math.abs(funnel.conversionRate - funnel.tenantAvgConversionRate)}%
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginTop: 12 }}>
            <Donut
              size={92}
              thickness={14}
              segments={[
                { value: funnel.conversionRate ?? 0, color: '#2F7A5B' },
                { value: 100 - (funnel.conversionRate ?? 0), color: '#EBE6D6' },
              ]}
              center={
                <div style={{ textAlign: 'center' }}>
                  <div className="num-display" style={{ fontSize: 22 }}>
                    {funnel.conversionRate != null ? `${funnel.conversionRate}%` : '—'}
                  </div>
                  <div style={{ fontSize: 9, color: 'var(--ink-3)' }}>conv.</div>
                </div>
              }
            />
            <div style={{ flex: 1, fontSize: 12 }}>
              <DotRow color="#2F7A5B" label="Became deals" value={funnel.becameDealsCount ?? 0} />
              <DotRow color="#EBE6D6" label="Did not convert" value={funnel.didNotConvertCount ?? 0} />
              {funnel.tenantAvgConversionRate != null && (
                <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.5 }}>
                  Tenant avg: {funnel.tenantAvgConversionRate}%
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Inbound feed + Top teams */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16 }}>
        <div className="card" style={{ overflow: 'hidden' }}>
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid var(--line)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <div style={{ fontWeight: 500, fontSize: 14 }}>Inbound feed · last 10</div>
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Live · 5s polling</span>
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 3,
                background: '#2F7A5B',
                marginLeft: 6,
              }}
            />
          </div>
          {loadingFeed ? (
            <EmptyState title="Loading…" />
          ) : feed.length === 0 ? (
            <EmptyState
              icon={<Inbox size={28} />}
              title="No inbound leads yet"
              sub="WhatsApp / IndiaMART / web leads land here in real-time."
            />
          ) : (
            <div>
              {feed.slice(0, 8).map((l, i) => {
                const last = i === Math.min(7, feed.length - 1);
                return (
                  <div
                    key={l.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '20px 1fr auto auto auto',
                      gap: 10,
                      alignItems: 'center',
                      padding: '8px 16px',
                      borderBottom: last ? 'none' : '1px solid var(--ivory-3)',
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                    onClick={() => navigate(`/lead-manager/leads/${l.id}`)}
                  >
                    <SourceIcon source={l.source} size={18} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <PriorityDot p={priorityFrom(l)} />
                      <span
                        style={{
                          fontWeight: 500,
                          color: 'var(--ink-0)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {leadName(l)}
                      </span>
                      <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>· {leadProduct(l)}</span>
                    </div>
                    <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                      {leadPhone(l)}
                    </span>
                    <StatusChip status={l.verificationStatus ?? l.status} />
                    <span
                      style={{
                        fontSize: 11,
                        color: 'var(--ink-3)',
                        fontVariantNumeric: 'tabular-nums',
                        minWidth: 48,
                        textAlign: 'right',
                      }}
                    >
                      {relativeTime(l.createdAt)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="card" style={{ overflow: 'hidden' }}>
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid var(--line)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <div style={{ fontWeight: 500, fontSize: 14 }}>Top teams · today</div>
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>by assignments</span>
          </div>
          {teams.length === 0 ? (
            <EmptyState title="No team data yet" sub="Teams appear once leads are assigned today." />
          ) : (
            <div style={{ padding: '4px 8px' }}>
              {(() => {
                const max = Math.max(1, ...teams.map((t) => t.assignmentsToday ?? 0));
                return teams.map((t, i) => (
                  <div key={t.teamId ?? i} style={{ padding: '8px 10px', borderRadius: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Avatar name={t.headName ?? t.teamName} size={22} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{t.teamName}</div>
                        <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                          {t.headName ?? '—'} · {t.weeklyConversion ?? 0}% wk conv.
                        </div>
                      </div>
                      <div className="num-display" style={{ fontSize: 18 }}>
                        {t.assignmentsToday ?? 0}
                      </div>
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <Progress value={((t.assignmentsToday ?? 0) / max) * 100} color="var(--saffron)" />
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
}

/* ─── Local atoms ──────────────────────────────────────────────────────── */

function DotRow({ color, label, value }: { color: string; label: string; value: string | number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
      <span style={{ flex: 1, color: 'var(--ink-2)' }}>{label}</span>
      <span className="num" style={{ color: 'var(--ink-0)', fontWeight: 500 }}>
        {value}
      </span>
    </div>
  );
}

function Progress({ value, color }: { value: number; color: string }) {
  const v = Math.min(100, Math.max(0, value));
  return (
    <div style={{ height: 4, background: 'var(--ivory-2)', borderRadius: 2, overflow: 'hidden' }}>
      <div style={{ width: `${v}%`, height: '100%', background: color, borderRadius: 2 }} />
    </div>
  );
}

function Donut({
  size,
  thickness,
  segments,
  center,
}: {
  size: number;
  thickness: number;
  segments: { value: number; color: string }[];
  center?: React.ReactNode;
}) {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        {segments.map((s, i) => {
          const dash = (s.value / total) * c;
          const seg = (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={thickness}
              strokeDasharray={`${dash} ${c}`}
              strokeDashoffset={-offset}
            />
          );
          offset += dash;
          return seg;
        })}
      </svg>
      {center && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {center}
        </div>
      )}
    </div>
  );
}

export default LeadManagerHome;
